/**
 * Product sorting utilities
 *
 * Provides shared sorting logic for products, especially handling
 * effective price calculation (sale price when on sale, otherwise regular price)
 *
 * Related files:
 * - Used in: ~/api/typesense.server.ts, ~/api/brand-filtering.server.ts, ~/api/category-filtering.server.ts, ~/api/shop-filtering.server.ts, ~/api/deals-filtering.server.ts
 * - Product type from: ~/api/typesense.server.ts
 */

/**
 * Minimal product interface required for price sorting
 * This allows the sorting function to work with different Product types across the codebase
 */
interface SortableProduct {
  price: number;
  salePrice?: number;
  onsale?: boolean;
}

/**
 * Get the effective price for a product
 * Uses sale price when product is on sale, otherwise uses regular price
 * This ensures products are sorted by the price customers actually see/pay
 *
 * IMPORTANT: When onsale=true, salePrice contains the sale price (lower price)
 * and price contains the regular price (higher price). We use salePrice for sorting.
 */
function getEffectivePrice(product: SortableProduct): number {
  // If product is on sale and has a valid sale price, use it for sorting
  // This ensures sale prices are compared correctly against regular prices
  // Example: Product with salePrice=$10 and price=$15 should sort as $10
  if (product.onsale && product.salePrice != null && product.salePrice > 0) {
    return product.salePrice;
  }

  // Fall back to regular price, with safety check for undefined/null
  // This handles products that are not on sale
  if (product.price != null && product.price > 0) {
    return product.price;
  }

  // Last resort: return 0 (shouldn't happen with valid data)
  return 0;
}

/**
 * Sort products by effective price (sale price when on sale, otherwise regular price)
 *
 * This function ensures that products with sale prices are correctly compared
 * against products with regular prices. For example:
 * - Product A: onsale=true, salePrice=$10, price=$15 → effective price = $10
 * - Product B: onsale=false, price=$12 → effective price = $12
 * When sorting low to high: A ($10) should come before B ($12)
 *
 * @param products - Array of products to sort (any type that has price, salePrice, and onsale fields)
 * @param direction - 'asc' for low to high, 'desc' for high to low
 * @returns New sorted array (does not mutate original)
 */
export function sortProductsByEffectivePrice<T extends SortableProduct>(
  products: T[],
  direction: 'asc' | 'desc',
): T[] {
  // Create a copy to avoid mutating the original array
  // IMPORTANT: We must re-sort ALL products to ensure correct ordering
  // because Typesense pre-sorts by treez_price_sell (regular price),
  // which doesn't account for sale prices
  return [...products].sort((a, b) => {
    const priceA = getEffectivePrice(a);
    const priceB = getEffectivePrice(b);

    // Handle equal prices - maintain original order (stable sort)
    if (priceA === priceB) {
      return 0;
    }

    // Compare effective prices (sale prices vs regular prices)
    // This ensures products with lower sale prices appear before products with higher regular prices
    return direction === 'asc' ? priceA - priceB : priceB - priceA;
  });
}
